import React, { useState, useEffect } from 'react';
import './PricingFormModal.css';

interface PricingFormModalProps {
  isOpen: boolean;
  type: 'category' | 'service';
  title: string;
  initialData?: {
    name?: string;
    service_name?: string;
    price?: number;
    description?: string;
    display_order?: number;
  };
  onSave: (data: any) => void;
  onClose: () => void;
}

const PricingFormModal: React.FC<PricingFormModalProps> = ({
  isOpen,
  type,
  title,
  initialData,
  onSave,
  onClose
}) => {
  const [formData, setFormData] = useState({
    name: '',
    service_name: '',
    price: 0,
    description: '',
    display_order: 0
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        service_name: initialData.service_name || '',
        price: initialData.price || 0,
        description: initialData.description || '',
        display_order: initialData.display_order || 0
      });
    } else {
      setFormData({
        name: '',
        service_name: '',
        price: 0,
        description: '',
        display_order: 0
      });
    }
  }, [initialData, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'price' || name === 'display_order' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (type === 'category') {
      if (!formData.name.trim()) {
        setError('Category name is required');
        return;
      }
      onSave({
        name: formData.name.trim(),
        display_order: formData.display_order
      });
    } else {
      if (!formData.service_name.trim()) {
        setError('Service name is required');
        return;
      }
      if (formData.price <= 0) {
        setError('Price must be greater than 0');
        return;
      }
      onSave({
        service_name: formData.service_name.trim(),
        price: formData.price,
        description: formData.description.trim() || null,
        display_order: formData.display_order
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="form-error">{error}</div>}

            {type === 'category' ? (
              <>
                <div className="form-group">
                  <label htmlFor="name">Category Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Emergency Care"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="display_order">Display Order</label>
                  <input
                    type="number"
                    id="display_order"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="service_name">Service Name *</label>
                  <input
                    type="text"
                    id="service_name"
                    name="service_name"
                    value={formData.service_name}
                    onChange={handleChange}
                    placeholder="e.g., Wellness Exam"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="price">Price ($) *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="description">Description (Optional)</label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Additional details about this service..."
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="display_order">Display Order</label>
                  <input
                    type="number"
                    id="display_order"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                    placeholder="0"
                    min="0"
                  />
                  <small>Lower numbers appear first within category</small>
                </div>
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-btn">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PricingFormModal;